/**
 * User Management API Client
 * Task 17.5: User管理UIの構築
 * Requirements: 20.2, 20.3, 20.4, 20.5, 20.6, 20.7, 20.8, 20.9
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

export interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  jobTitle?: string;
  role: 'USER' | 'ADMIN';
  permissionLevel: 'SELF_ONLY' | 'DEPARTMENT' | 'COMPANY_WIDE';
  company?: {
    id: string;
    name: string;
  };
  employeeDepartments?: Array<{
    department: {
      id: string;
      name: string;
    };
    isPrimary: boolean;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface GetUsersParams {
  companyId?: string;
  departmentId?: string;
  username?: string;
  email?: string;
  permissionLevel?: string;
  limit?: number;
  offset?: number;
}

export interface GetUsersResponse {
  users: User[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface CreateUserInput {
  username: string;
  email: string;
  name: string;
  jobTitle?: string;
  role?: 'USER' | 'ADMIN';
  permissionLevel?: 'SELF_ONLY' | 'DEPARTMENT' | 'COMPANY_WIDE';
  companyId: string;
  departmentId?: string;
  isPrimaryDepartment?: boolean;
}

export interface UpdateUserInput {
  name?: string;
  jobTitle?: string;
  permissionLevel?: 'SELF_ONLY' | 'DEPARTMENT' | 'COMPANY_WIDE';
  role?: 'USER' | 'ADMIN';
}

export interface BulkUpdateUsersInput {
  userIds: string[];
  companyId?: string;
  departmentId?: string;
}

export interface BulkUpdateResult {
  totalCount: number;
  successCount: number;
  failedCount: number;
  results: Array<{
    userId: string;
    status: 'success' | 'failed';
    error?: string;
  }>;
}

/**
 * Get users with filters and pagination
 */
export async function getUsers(params: GetUsersParams): Promise<GetUsersResponse> {
  const client = getApiClient();
  const response = await client.get<GetUsersResponse>('/admin/users', {
    params,
  });
  return response.data;
}

/**
 * Get user by ID
 */
export async function getUserById(id: string): Promise<User> {
  const client = getApiClient();
  const response = await client.get<{ user: User }>(`/admin/users/${id}`);
  return response.data.user;
}

/**
 * Create new user
 */
export async function createUser(input: CreateUserInput): Promise<User> {
  const client = getApiClient();
  const response = await client.post<User>('/admin/users', input);
  return response.data;
}

/**
 * Update user
 */
export async function updateUser(id: string, input: UpdateUserInput): Promise<User> {
  const client = getApiClient();
  const response = await client.put<{ user: User }>(`/admin/users/${id}`, input);
  return response.data.user;
}

/**
 * Delete user
 */
export async function deleteUser(id: string): Promise<void> {
  const client = getApiClient();
  await client.delete(`/admin/users/${id}`);
}

/**
 * Bulk update users - change company and/or department for multiple users
 */
export async function bulkUpdateUsers(input: BulkUpdateUsersInput): Promise<BulkUpdateResult> {
  const client = getApiClient();
  const response = await client.put<BulkUpdateResult>('/admin/users/bulk', input);
  return response.data;
}

/**
 * Change user password (admin only)
 * Requirements: 3.1
 */
export interface ChangePasswordInput {
  newPassword: string;
  forcePasswordReset: boolean;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

export async function changeUserPassword(
  userId: string,
  input: ChangePasswordInput
): Promise<ChangePasswordResponse> {
  const client = getApiClient();
  const response = await client.put<ChangePasswordResponse>(
    `/admin/users/${userId}/password`,
    input
  );
  return response.data;
}
